import { TFunction } from 'i18next';
import * as Sentry from '@sentry/react';
import { CodeExceptionStatus } from '../../api/models/CodeExceptionStatus';

/**
 * Buying a pass can fail two different ways on the server, and they arrive in different shapes:
 *
 *  - `pass_slot_at_capacity` and `pass_dated_session_unavailable` come back as a structured `code`
 *    (ApplyCodeException) because someone else took the last seat in a weekly slot, or a picked dated
 *    session filled up, since the availability was fetched.
 *  - the schedule validator raises plain HTTPExceptions, so its reason is only in `detail`.
 *
 * Everything here is customer-facing, so an unrecognised failure returns the generic purchase error
 * rather than leaking a server string.
 */

const detailOf = (e: any): string => {
  if (e?.status !== 400) return '';
  const detail = e?.body?.detail;
  return typeof detail === 'string' ? detail : '';
};

export const isSlotCapacityError = (e: any): boolean =>
  e?.status === 400 && e?.body?.code === CodeExceptionStatus.PASS_SLOT_AT_CAPACITY;

export const isDatedSessionUnavailableError = (e: any): boolean =>
  e?.status === 400 && e?.body?.code === CodeExceptionStatus.PASS_DATED_SESSION_UNAVAILABLE;

/**
 * A message for a failed `createPass`, or null when this mapper does not recognise the failure --
 * callers fall back to their own generic copy so each surface keeps its own wording.
 */
export const passPurchaseErrorMessage = (e: any, t: TFunction): string | null => {
  if (isSlotCapacityError(e)) {
    return t('pass_schedule.errors.slotAtCapacity');
  }
  if (isDatedSessionUnavailableError(e)) {
    return t('pass_dated_sessions.errors.sessionUnavailable');
  }

  const detail = detailOf(e);
  if (!detail) return null;

  if (
    detail.startsWith('scheduleSelectionsCountMismatch') ||
    detail.startsWith('datedSessionSelectionsCountMismatch')
  ) {
    return t('pass_schedule.errors.countMismatch');
  }
  if (detail === 'scheduleSelectionsRequired') {
    return t('pass_schedule.errors.scheduleSelectionsRequired');
  }
  if (detail === 'scheduleSelectionsNotAllowedForPassType') {
    return t('pass_schedule.errors.scheduleSelectionsNotAllowedForPassType');
  }
  // These mean the plan itself is set up wrong -- nothing the customer can fix, so they get a
  // "contact the studio" message and we get a Sentry report.
  if (
    detail === 'fixedScheduleMonthlyMissingWeeklySessionCount' ||
    detail === 'scheduledWeeklyRequiresEventTarget' ||
    detail === 'datedSessionsRequiresEventTarget' ||
    detail === 'datedSessionsRequiresPunchBasedPass' ||
    detail === 'datedSessionOutsidePassValidity'
  ) {
    Sentry.captureException(e);
    return t('pass_schedule.errors.planMisconfigured');
  }

  return null;
};
